import NotificationPermissionManager from "@/components/NotificationPermissionManager";
import { User, onAuthStateChanged, signOut } from "firebase/auth";
import { isEqual } from "lodash";
import { useRouter } from "next/router";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from "react";
import { ToastContainer } from "react-toastify";
import { RootAction } from "../actions";
import { LoadingPage } from "../components/commons/LoadingPage";
import useRouterChange from "../hooks/useRouterChange";
import { auth } from "../lib/firebaseConfig";
import { listenUser } from "../pages/api/listenUser";
import { reducer } from "../reducers";
import { RootState } from "../types/state";
import {
  CHAT_DETAIL_PATH,
  NAVBAR_LINK,
  PUBLIC_PATHS,
  STORAGE_KEY,
  USER_DETAIL_PATH,
  USER_SEX
} from "../utils/enum";
import { initialState } from "../utils/initialState";
import { initializeRouteUtils, setGlobalPrevRoute } from "../utils/routeUtils";

// FIXME:型
export const RootContext = createContext<{
  state: RootState;
  dispatch: React.Dispatch<RootAction>;
}>({ state: initialState, dispatch: () => null });

interface RootProviderProps {
  children: ReactNode;
}

export const RootProvider = ({ children }: RootProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { authResolved, isLoggedIn } = state;
  const router = useRouter();
  const currentPathRef = useRef(router.asPath);
  const stateRef = useRef(state);
  const routeChanging = useRouterChange(setGlobalPrevRoute, currentPathRef);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Initialize global route utils
  useEffect(() => {
    initializeRouteUtils(dispatch, state);
  }, [state]);

  // Update current path ref
  useEffect(() => {
    currentPathRef.current = router.asPath;
  }, [router.asPath]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, handleAuthStateChange);

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!routeChanging && typeof window !== "undefined") {
      handleDomainRedirect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeChanging]);

  const isPublicPage = useMemo(() => {
    const path = router.asPath.split("?")[0];
    return PUBLIC_PATHS.includes(path);
  }, [router.asPath]);

  const handleAuthStateChange = useCallback(
    async (user: User | null) => {
      dispatch({
        type: "setFirebaseUser",
        payload: {
          ...stateRef.current,
          authResolved: true,
          firebaseUser: user
        }
      });
      if (user) {
        handleUserLoggedIn(user);
        return;
      }

      const { isLoggedIn: isLoggedInState } = stateRef.current;
      if (isLoggedInState) {
        const path = window.location.pathname.split("?")[0];
        const isDefaultPage = NAVBAR_LINK.defaultPage === path;
        const isDeletedPage = NAVBAR_LINK.deletionSuccess === path;
        if (!isDefaultPage && !isDeletedPage) {
          router.push(NAVBAR_LINK.defaultPage);
        }
      } else {
        if (!isPublicPage) {
          const href = window.location.href;
          const indexOfPath = href.indexOf(window.location.pathname);
          handleUserLoggedOut(href.slice(indexOfPath, href.length));
        }
      }
      dispatch({
        type: "setUserInfo",
        payload: {
          ...stateRef.current,
          isLoggedIn: false,
          user: initialState.user,
          fcmToken: null
        }
      });
    },
    [isPublicPage, router]
  );

  const hydrateCanonicalUser = useCallback(async (firebaseUser: User) => {
    return listenUser(firebaseUser.uid, () => null, true);
  }, []);

  const handleUserLoggedIn = async (user: User) => {
    localStorage.setItem("uid", user.uid);
    const path = window.location.pathname.split("?")[0];
    const { userInfo: collectionUser, member } = await hydrateCanonicalUser(user);
    if (collectionUser.sex) {
      if (collectionUser.sex === USER_SEX.male) {
        localStorage.removeItem(`${STORAGE_KEY.SKIP_NEW_ONBOARDING}-${user?.uid}`);
      }
      dispatch({
        type: "setUserInfo",
        payload: {
          ...stateRef.current,
          user: isEqual(collectionUser, stateRef.current.user)
            ? stateRef.current.user
            : collectionUser,
          member,
          isLoggedIn: true
        }
      });
    } else if (path !== NAVBAR_LINK.signIn && path !== NAVBAR_LINK.signUp) {
      try {
        await signOut(auth);
      } catch (_) { }
    }
  };

  const handleUserLoggedOut = (href: string) => {
    const path = href.split("?")[0];
    const isSignInPage = NAVBAR_LINK.signIn === path;
    if (!PUBLIC_PATHS.includes(path)) {
      if (!isSignInPage) {
        const skipUrls = [USER_DETAIL_PATH, CHAT_DETAIL_PATH];
        if (!skipUrls.includes(href))
          router.push({
            pathname: NAVBAR_LINK.signIn,
            query: { returnUrl: href }
          });
      }
    }
  };

  const handleDomainRedirect = () => {
    if (window.location.host === "uraharu-6cbff.web.app") {
      window.location.replace(
        `https://uraharu.xyz${window.location.pathname}${window.location.search}`
      );
    } else {
      if (!authResolved) return;
      if (!isLoggedIn && !stateRef.current.firebaseUser) {
        if (!localStorage.getItem("justLoggedOut")) {
          handleUserLoggedOut(router.asPath);
        } else localStorage.removeItem("justLoggedOut");
      }
    }
  };

  return (
    <RootContext.Provider value={{ state, dispatch }}>
      {authResolved && ((Boolean(state.firebaseUser) && Boolean(state.user.uid)) || isPublicPage) ? (
        children
      ) : (
        <LoadingPage />
      )}
      <ToastContainer />
      <NotificationPermissionManager />
    </RootContext.Provider>
  );
};
